<template>
  <label
    class="tw-inline-flex tw-items-center tw-cursor-pointer"
    :class="{ 'tw-opacity-50 tw-cursor-not-allowed': disabled }"
  >
    <input
      type="checkbox"
      :checked="isChecked"
      :indeterminate="indeterminate"
      :disabled="disabled"
      @change="handleChange"
      class="tw-appearance-none"
      ref="checkboxRef"
    />
    <div
      class="tw-relative tw-w-5 tw-h-5 tw-border-2 tw-rounded tw-transition-colors hover:tw-ring-2 hover:tw-ring-primary-300 focus-within:tw-ring-2 focus-within:tw-ring-primary-300"
      :class="[
        isChecked || indeterminate
          ? disabled
            ? 'tw-bg-gray-400 tw-border-gray-400'
            : 'tw-bg-primary-500 tw-border-primary-500'
          : 'tw-bg-white tw-border-gray-300',
        !disabled &&
          'hover:tw-border-gray-400 focus-within:tw-ring-2 focus-within:tw-ring-primary-300',
      ]"
    >
      <!-- Checkmark icon -->
      <svg
        v-if="isChecked && !indeterminate"
        class="tw-absolute tw-w-3 tw-h-3 tw-top-0.5 tw-left-0.5 tw-text-white tw-transition-opacity tw-pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
      </svg>

      <!-- Minus icon for indeterminate -->
      <svg
        v-if="indeterminate"
        class="tw-absolute tw-w-3 tw-h-3 tw-top-0.5 tw-left-0.5 tw-text-white tw-transition-opacity tw-pointer-events-none"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 12h14" />
      </svg>
    </div>
    <span
      v-if="label"
      class="tw-ml-2 tw-text-gray-700 tw-select-none"
      :class="{ 'tw-text-gray-400': disabled }"
    >
      {{ label }}
    </span>
  </label>
</template>

<script setup>
import { ref, watch, onMounted, computed } from 'vue';

const props = defineProps({
  // Support both v-model and checked prop
  modelValue: {
    type: Boolean,
    default: undefined,
  },
  checked: {
    type: Boolean,
    default: false,
  },
  indeterminate: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const checkboxRef = ref(null);

// Use modelValue if provided (v-model), otherwise use checked prop
const isChecked = computed(() => {
  return props.modelValue !== undefined ? props.modelValue : props.checked;
});

const handleChange = event => {
  const newValue = event.target.checked;

  // Emit both events for compatibility
  emit('update:modelValue', newValue);
  emit('change', event);
};

// Set indeterminate state on the native checkbox element
watch(
  () => props.indeterminate,
  newVal => {
    if (checkboxRef.value) {
      checkboxRef.value.indeterminate = newVal;
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (checkboxRef.value && props.indeterminate) {
    checkboxRef.value.indeterminate = true;
  }
});
</script>
